import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bell, MessageSquare, Send, Users, Building2 } from 'lucide-react';
import { getClinics, sendCustomNotification, bulkSendSms } from '../../services/api';
import { toast } from 'sonner';

export const AdminNotificationsPanel = () => {
  const [clinics, setClinics] = useState([]);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMessage, setNotifMessage] = useState('');
  const [notifClinic, setNotifClinic] = useState('all');

  const [smsTarget, setSmsTarget] = useState('customers');
  const [smsClinic, setSmsClinic] = useState('all');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    getClinics().then(r => setClinics(r.data)).catch(() => {});
  }, []);

  const handleSendNotif = async () => {
    if (!notifTitle || !notifMessage) { toast.error('Başlık ve mesaj gerekli'); return; }
    try {
      await sendCustomNotification({ title: notifTitle, message: notifMessage, clinic_id: notifClinic === 'all' ? null : notifClinic });
      toast.success('Bildirim gönderildi');
      setNotifTitle('');
      setNotifMessage('');
    } catch (e) { toast.error('Hata'); }
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim()) { toast.error('Mesaj boş olamaz'); return; }
    setSmsSending(true);
    try {
      const res = await bulkSendSms({
        target_type: smsTarget,
        clinic_id: smsClinic === 'all' ? null : smsClinic,
        message: smsMessage,
      });
      if (res.data.success) {
        toast.success(`SMS gönderildi (${res.data.phone_count} numara)`);
        setSmsMessage('');
      } else {
        toast.error(res.data.message || 'SMS gönderilemedi');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SMS gönderim hatası');
    } finally { setSmsSending(false); }
  };

  const charCount = smsMessage.length;
  const smsCount = charCount === 0 ? 0 : Math.ceil(charCount / 160);

  return (
    <div className="space-y-4">
      {/* Uygulama Bildirimi */}
      <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#6b6de3]/10 flex items-center justify-center">
            <Bell size={16} className="text-[#6b6de3]" />
          </div>
          <h3 className="font-semibold text-gray-900">Uygulama Bildirimi</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#8E8E93]">Klinik</Label>
            <Select value={notifClinic} onValueChange={setNotifClinic}>
              <SelectTrigger className="h-12 bg-[#F2F2F7] border-0 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Klinikler</SelectItem>
                {clinics.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#8E8E93]">Başlık</Label>
            <Input value={notifTitle} onChange={e => setNotifTitle(e.target.value)} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" data-testid="notif-title-input" />
          </div>
          <div>
            <Label className="text-xs text-[#8E8E93]">Mesaj</Label>
            <textarea value={notifMessage} onChange={e => setNotifMessage(e.target.value)} className="w-full h-24 bg-[#F2F2F7] border-0 rounded-xl p-3 text-sm resize-none" data-testid="notif-message-input" />
          </div>
          <Button onClick={handleSendNotif} className="w-full bg-[#6b6de3] rounded-full" data-testid="notif-send-btn">
            <Bell size={14} className="mr-2" /> Bildirim Gönder
          </Button>
        </div>
      </div>

      {/* SMS Gönder (NetGSM) */}
      <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <MessageSquare size={16} className="text-emerald-600" />
          </div>
          <h3 className="font-semibold text-gray-900">SMS Gönder</h3>
          <span className="text-[10px] text-[#8E8E93] bg-[#F2F2F7] px-2 py-0.5 rounded-full">NetGSM</span>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#8E8E93]">Hedef</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button onClick={() => setSmsTarget('customers')}
                className={`flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-medium transition-all ${smsTarget === 'customers' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-[#F2F2F7] text-gray-600'}`}
                data-testid="sms-target-customers">
                <Users size={16} /> Müşteriler
              </button>
              <button onClick={() => setSmsTarget('clinics')}
                className={`flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-medium transition-all ${smsTarget === 'clinics' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-[#F2F2F7] text-gray-600'}`}
                data-testid="sms-target-clinics">
                <Building2 size={16} /> Klinikler
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#8E8E93]">Klinik</Label>
            <Select value={smsClinic} onValueChange={setSmsClinic}>
              <SelectTrigger className="h-12 bg-[#F2F2F7] border-0 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Klinikler</SelectItem>
                {clinics.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#8E8E93]">Mesaj</Label>
            <textarea value={smsMessage} onChange={e => setSmsMessage(e.target.value)}
              placeholder="SMS metnini yazın..."
              className="w-full h-28 bg-[#F2F2F7] border-0 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-emerald-500/30 outline-none"
              data-testid="sms-message-input" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-[#8E8E93]">{charCount} karakter</span>
              <span className="text-xs text-[#8E8E93]">{smsCount} SMS</span>
            </div>
          </div>
          <Button onClick={handleSendSms} disabled={smsSending || !smsMessage.trim()}
            className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full disabled:opacity-50" data-testid="sms-send-btn">
            {smsSending
              ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Gönderiliyor...</span>
              : <span className="flex items-center gap-2"><Send size={14} /> SMS Gönder</span>}
          </Button>
        </div>
      </div>
    </div>
  );
};
